import React, { useState, useEffect } from 'react';
import SidebarHeader from '../../components/admin/sidebar';
import { useAuth } from '../../pages/auth/AuthContext';
import RequireAdminRole from '../../components/auth/userole';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  Package,
  DollarSign,
  Zap,
  MapPin,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Plus
} from 'lucide-react';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const CATEGORY_OPTIONS = [
  { value: '', label: 'Semua Kategori' },
  { value: 'home', label: 'Home' },
  { value: 'business', label: 'Business' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'premium', label: 'Premium' },
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'streaming', label: 'Streaming' },
  { value: 'enterprise', label: 'Enterprise' }
];

const AdminPackages = () => {
  const { user, loading } = useAuth();
  const { user: contextUser, token: contextToken } = useAuth();
  
  const [activeTab, setActiveTab] = useState('packages');
  const [isLoading, setIsLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: contextUser?.fullName || contextUser?.name || 'Admin User',
    email: contextUser?.email || 'admin@telnet.co.id',
    role: contextUser?.role || 'Super Admin'
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  // Form data for create/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'home',
    speed: '',
    upload: '',
    price: {
      amount: '',
      currency: 'IDR',
      displayPrice: '',
      originalAmount: '',
      originalDisplayPrice: ''
    },
    features: [''],
    specifications: {
      downloadSpeed: { value: '', unit: 'Mbps' },
      uploadSpeed: { value: '', unit: 'Mbps' },
      maxDevices: '',
      dataQuota: 'Unlimited',
      latency: { value: '', unit: 'ms' }
    },
    availability: {
      cities: [''],
      isActive: true
    },
    popular: false,
    badge: '',
    color: '',
    sortOrder: 0
  });

  const LIMIT = 10;

  // Token handling
  const authToken = contextToken ||
                    localStorage.getItem('adminToken') ||
                    localStorage.getItem('token') ||
                    sessionStorage.getItem('adminToken') ||
                    sessionStorage.getItem('token');

  // Auth headers function
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    return headers;
  };

  // Format currency IDR
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000000) {
      return `Rp ${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `Rp ${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `Rp ${(amount / 1000).toFixed(1)}K`;
    }
    return formatCurrency(amount);
  };

  // Fetch packages
  const fetchPackages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: LIMIT.toString(),
        ...(categoryFilter && { category: categoryFilter }),
        ...(activeFilter !== '' && { active: activeFilter }),
        ...(search && { search })
      });

      const headers = getAuthHeaders();
      const packagesUrl = `${API_BASE_URL}/api/packages?${params.toString()}`;

      const response = await fetch(packagesUrl, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view packages.');
        } else if (response.status === 404) {
          throw new Error('Packages endpoint not found.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch packages`);
        }
      }

      const data = await response.json();

      if (data.success) {
        const packagesData = Array.isArray(data.data) ? data.data : [];
        setPackages(packagesData);
        setTotalPages(data.pagination?.pages || Math.ceil(packagesData.length / LIMIT) || 1);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load packages');
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      
      let errorMessage = error.message;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setPackages([]);
      setTotalPages(1);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Create package
  const handleCreatePackage = async () => {
    try {
      if (!formData.name || !formData.description || !formData.speed) {
        alert('Name, description, dan speed harus diisi');
        return;
      }

      if (!formData.price.amount || !formData.price.displayPrice) {
        alert('Harga harus diisi');
        return;
      }

      if (!formData.features.some(f => f.trim())) {
        alert('Minimal satu fitur harus diisi');
        return;
      }

      const cleanedData = {
        ...formData,
        features: formData.features.filter(f => f.trim()),
        availability: {
          ...formData.availability,
          cities: formData.availability.cities.filter(c => c.trim())
        }
      };

      const response = await fetch(`${API_BASE_URL}/api/packages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify(cleanedData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to create package`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Paket berhasil dibuat!');
        setShowCreateModal(false);
        resetForm();
        fetchPackages();
      } else {
        throw new Error(data.message || 'Failed to create package');
      }
    } catch (error) {
      console.error('Error creating package:', error);
      alert('Error creating package: ' + error.message);
    }
  };

  // Update package
  const handleUpdatePackage = async () => {
    if (!selectedPackage) return;

    try {
      const cleanedData = {
        ...formData,
        features: formData.features.filter(f => f.trim()),
        availability: {
          ...formData.availability,
          cities: formData.availability.cities.filter(c => c.trim())
        }
      };

      const response = await fetch(`${API_BASE_URL}/api/packages/${selectedPackage._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify(cleanedData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to update package`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Paket berhasil diupdate!');
        setShowEditModal(false);
        setSelectedPackage(null);
        resetForm();
        fetchPackages();
      } else {
        throw new Error(data.message || 'Failed to update package');
      }
    } catch (error) {
      console.error('Error updating package:', error);
      alert('Error updating package: ' + error.message);
    }
  };

  // Delete package
  const handleDelete = async (pkg) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus paket "${pkg.name}"?`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/packages/${pkg._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to delete package`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Paket berhasil dihapus!');
        fetchPackages();
      } else {
        throw new Error(data.message || 'Failed to delete package');
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Error deleting package: ' + error.message);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'home',
      speed: '',
      upload: '',
      price: {
        amount: '',
        currency: 'IDR',
        displayPrice: '',
        originalAmount: '',
        originalDisplayPrice: ''
      },
      features: [''],
      specifications: {
        downloadSpeed: { value: '', unit: 'Mbps' },
        uploadSpeed: { value: '', unit: 'Mbps' },
        maxDevices: '',
        dataQuota: 'Unlimited',
        latency: { value: '', unit: 'ms' }
      },
      availability: {
        cities: [''],
        isActive: true
      },
      popular: false,
      badge: '',
      color: '',
      sortOrder: 0
    });
  };

  // Open modals
  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (pkg) => {
    setSelectedPackage(pkg);
    setFormData({
      name: pkg.name || '',
      description: pkg.description || '',
      category: pkg.category || 'home',
      speed: pkg.speed || '',
      upload: pkg.upload || '',
      price: {
        amount: pkg.price?.amount || '',
        currency: pkg.price?.currency || 'IDR',
        displayPrice: pkg.price?.displayPrice || '',
        originalAmount: pkg.price?.originalAmount || '',
        originalDisplayPrice: pkg.price?.originalDisplayPrice || ''
      },
      features: pkg.features && pkg.features.length > 0 ? pkg.features : [''],
      specifications: {
        downloadSpeed: { 
          value: pkg.specifications?.downloadSpeed?.value || '', 
          unit: pkg.specifications?.downloadSpeed?.unit || 'Mbps' 
        },
        uploadSpeed: { 
          value: pkg.specifications?.uploadSpeed?.value || '', 
          unit: pkg.specifications?.uploadSpeed?.unit || 'Mbps' 
        },
        maxDevices: pkg.specifications?.maxDevices || '',
        dataQuota: pkg.specifications?.dataQuota || 'Unlimited',
        latency: { 
          value: pkg.specifications?.latency?.value || '', 
          unit: pkg.specifications?.latency?.unit || 'ms' 
        }
      },
      availability: {
        cities: pkg.availability?.cities && pkg.availability.cities.length > 0 ? pkg.availability.cities : [''],
        isActive: pkg.availability?.isActive !== false
      },
      popular: pkg.popular || false,
      badge: pkg.badge || '',
      color: pkg.color || '',
      sortOrder: pkg.sortOrder || 0
    });
    setShowEditModal(true);
  };

  const openDetailModal = (pkg) => {
    setSelectedPackage(pkg);
    setShowDetailModal(true);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleArrayInputChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Update current user when context changes
  useEffect(() => {
    if (contextUser) {
      setCurrentUser({
        name: contextUser.fullName || contextUser.name || 'Admin User',
        email: contextUser.email || 'admin@telnet.co.id',
        role: contextUser.role || 'Super Admin'
      });
    }
  }, [contextUser]);

  useEffect(() => {
    fetchPackages();
  }, [page, categoryFilter, activeFilter, authToken]);

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPackages();
  };

  // Pagination controls
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Retry function for failed requests
  const handleRetry = () => {
    fetchPackages();
  };

  // Mobile Header Component with green theme
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Manage Packages</h1>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {error && (
            <button
              onClick={handleRetry}
              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition text-xs"
            >
              Retry
            </button>
          )}
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  // Stats Cards Component
  const StatsCards = () => {
    const stats = {
      total: packages.length,
      active: packages.filter(pkg => pkg.availability?.isActive).length,
      popular: packages.filter(pkg => pkg.popular).length,
      revenue: packages.reduce((sum, pkg) => sum + (pkg.price?.amount || 0), 0)
    };

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Packages</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Package className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Popular</p>
              <p className="text-2xl font-bold text-gray-900">{stats.popular}</p>
            </div>
            <Zap className="text-yellow-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrencyShort(stats.revenue)}</p>
              <button 
                onClick={fetchPackages}
                disabled={isLoading}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
              >
                <RefreshCw size={12} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            <DollarSign className="text-purple-500" size={32} />
          </div>
        </div>
      </div>
    );
  };

  // Mobile Package Card Component with green theme
  const MobilePackageCard = ({ pkg }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => openDetailModal(pkg)}
            className="text-sm font-medium text-green-600 hover:text-green-800 truncate block text-left"
          >
            {pkg.name}
          </button>
          <p className="text-sm text-gray-600 truncate capitalize">{pkg.category}</p>
          <p className="text-xs text-gray-500">{pkg.speed}</p>
        </div>
        <div className="flex items-center space-x-1 ml-2">
          {pkg.availability?.isActive ? (
            <CheckCircle size={14} className="text-green-600" />
          ) : (
            <XCircle size={14} className="text-red-600" />
          )}
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
            pkg.availability?.isActive 
              ? 'bg-green-100 text-green-800 border-green-200' 
              : 'bg-red-100 text-red-800 border-red-200'
          }`}>
            {pkg.availability?.isActive ? 'Aktif' : 'Nonaktif'}
          </span>
        </div>
      </div>
      
      <div className="flex justify-between items-center mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 truncate">{formatCurrency(pkg.price?.amount || 0)}</p>
          <p className="text-xs text-gray-500">{pkg.price?.displayPrice}</p>
        </div>
        {pkg.popular && (
          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full border border-yellow-200">
            Popular
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => openEditModal(pkg)}
          className="flex-1 min-w-0 text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center justify-center space-x-1"
        >
          <Edit size={12} />
          <span>Edit</span>
        </button>
        <button
          onClick={() => openDetailModal(pkg)}
          className="flex-1 min-w-0 text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center justify-center space-x-1"
        >
          <Eye size={12} />
          <span>Detail</span>
        </button>
        <button
          onClick={() => handleDelete(pkg)}
          className="flex-1 min-w-0 text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center justify-center space-x-1"
        >
          <Trash2 size={12} />
          <span>Hapus</span>
        </button>
      </div>
    </div>
  );

  // Desktop Table Component with green theme
  const DesktopTable = () => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama Paket</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategori</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kecepatan</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Harga</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {packages.map((pkg) => (
            <tr key={pkg._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 cursor-pointer hover:text-green-800"
                  onClick={() => openDetailModal(pkg)}>
                <div className="flex items-center space-x-2">
                  <Package size={16} className="text-gray-400" />
                  <span>{pkg.name}</span>
                  {pkg.popular && (
                    <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full border border-yellow-200">
                      Popular
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{pkg.category}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Zap size={14} className="text-gray-400" />
                  <span>{pkg.speed}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                <div className="flex items-center space-x-2">
                  <DollarSign size={14} className="text-gray-400" />
                  <span>{formatCurrency(pkg.price?.amount || 0)}</span>
                </div>
                {pkg.price?.displayPrice && (
                  <div className="text-xs text-gray-500">{pkg.price.displayPrice}</div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  {pkg.availability?.isActive ? (
                    <CheckCircle size={14} className="text-green-600" />
                  ) : (
                    <XCircle size={14} className="text-red-600" />
                  )}
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                    pkg.availability?.isActive 
                      ? 'bg-green-100 text-green-800 border-green-200' 
                      : 'bg-red-100 text-red-800 border-red-200'
                  }`}>
                    {pkg.availability?.isActive ? 'Aktif' : 'Nonaktif'}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex space-x-1 justify-center">
                  <button
                    onClick={() => openEditModal(pkg)}
                    className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center space-x-1"
                    title="Edit Package"
                  >
                    <Edit size={12} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => openDetailModal(pkg)}
                    className="text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center space-x-1"
                    title="View Details"
                  >
                    <Eye size={12} />
                    <span>Detail</span>
                  </button>
                  <button
                    onClick={() => handleDelete(pkg)}
                    className="text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center space-x-1"
                    title="Delete Package"
                  >
                    <Trash2 size={12} />
                    <span>Hapus</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <RequireAdminRole user={user} loading={loading}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar - Mobile responsive */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            currentUser={currentUser}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />

          <main className="flex-1 overflow-auto p-3 sm:p-6">
            {/* Main Panel dengan green theme */}
            <div className="bg-white rounded-lg shadow-xl">
              {/* Header dengan gradient hijau */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Package className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h1 className="font-bold text-xl">Package Management</h1>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-green-100">
                        {packages.length} packages ditemukan • Page {page} dari {totalPages}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchPackages}
                    disabled={isLoading}
                    className="hover:bg-green-700 p-2 rounded transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={openCreateModal}
                    className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Plus size={20} />
                    <span>Tambah Paket</span>
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="p-4 border-b border-gray-200">
                <StatsCards />
              </div>

              {/* Filters and Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <form onSubmit={handleSearchSubmit} className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Cari berdasarkan nama paket..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </form>

                  {/* Filter Toggle untuk Mobile */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Filter size={20} />
                    <span>Filter</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {/* Filters */}
                  <div className={`flex flex-col lg:flex-row gap-2 ${showFilters ? 'block' : 'hidden lg:flex'}`}>
                    <select
                      value={categoryFilter}
                      onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={activeFilter}
                      onChange={(e) => {
                        setActiveFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Semua Status</option>
                      <option value="true">Aktif</option>
                      <option value="false">Nonaktif</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mx-4 mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span>⚠️</span>
                      <span>{error}</span>
                    </div>
                    <button
                      onClick={handleRetry}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Coba Lagi
                    </button>
                  </div>
                </div>
              )}

              {/* Packages Content */}
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={32} className="animate-spin text-green-600 mb-2" />
                      <span className="text-gray-500">Loading packages...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center max-w-md mx-auto">
                      <div className="text-red-600 mb-4">
                        <span className="text-4xl">⚠️</span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Packages</h3>
                      <p className="text-gray-600 mb-4">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                ) : packages.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <Package size={64} className="mx-auto opacity-30" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Packages Found</h3>
                    <p className="text-gray-600">
                      {search || categoryFilter || activeFilter ? 'Try adjusting your search filters' : 'No packages have been created yet'}
                    </p>
                    <button
                      onClick={openCreateModal}
                      className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                    >
                      Create First Package
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Mobile Cards */}
                    <div className="sm:hidden">
                      {packages.map((pkg) => (
                        <MobilePackageCard key={pkg._id} pkg={pkg} />
                      ))}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                      <DesktopTable />
                    </div>
                  </>
                )}

                {/* Pagination */}
                {!error && !isLoading && packages.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-700">
                      Halaman {page} dari {totalPages} ({packages.length} packages)
                    </span>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Berikutnya
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Create Package Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Tambah Paket Baru</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nama Paket *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Masukkan nama paket"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Deskripsi *</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows="3"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Masukkan deskripsi paket"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kategori</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        {CATEGORY_OPTIONS.slice(1).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kecepatan *</label>
                      <input
                        type="text"
                        value={formData.speed}
                        onChange={(e) => handleInputChange('speed', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 100 Mbps"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Upload Speed</label>
                      <input
                        type="text"
                        value={formData.upload}
                        onChange={(e) => handleInputChange('upload', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 50 Mbps"
                      />
                    </div>
                  </div>
                  
                  {/* Price Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Harga (Rp) *</label>
                      <input
                        type="number"
                        value={formData.price.amount}
                        onChange={(e) => handleNestedInputChange('price', 'amount', parseFloat(e.target.value) || '')}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 899000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Display Price *</label>
                      <input
                        type="text"
                        value={formData.price.displayPrice}
                        onChange={(e) => handleNestedInputChange('price', 'displayPrice', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 899K/bulan"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Harga Asli (Opsional)</label>
                      <input
                        type="number"
                        value={formData.price.originalAmount}
                        onChange={(e) => handleNestedInputChange('price', 'originalAmount', parseFloat(e.target.value) || '')}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 1099000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Original Display Price (Opsional)</label>
                      <input
                        type="text"
                        value={formData.price.originalDisplayPrice}
                        onChange={(e) => handleNestedInputChange('price', 'originalDisplayPrice', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 1.099K/bulan"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.popular}
                          onChange={(e) => handleInputChange('popular', e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Popular</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.availability.isActive}
                          onChange={(e) => handleNestedInputChange('availability', 'isActive', e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Aktif</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Features */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fitur *</label>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleArrayInputChange('features', index, e.target.value)}
                        className="flex-grow border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Masukkan fitur"
                      />
                      {formData.features.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem('features', index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Hapus
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('features')}
                    className="text-green-600 hover:text-green-800 text-sm flex items-center space-x-1"
                  >
                    <Plus size={14} />
                    <span>Tambah Fitur</span>
                  </button>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePackage}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Buat Paket
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Package Modal */}
        {showEditModal && selectedPackage && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Edit Paket</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nama Paket *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Masukkan nama paket"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Deskripsi *</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows="3"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Masukkan deskripsi paket"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kategori</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        {CATEGORY_OPTIONS.slice(1).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kecepatan *</label>
                      <input
                        type="text"
                        value={formData.speed}
                        onChange={(e) => handleInputChange('speed', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 100 Mbps"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Upload Speed</label>
                      <input
                        type="text"
                        value={formData.upload}
                        onChange={(e) => handleInputChange('upload', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 50 Mbps"
                      />
                    </div>
                  </div>
                  
                  {/* Price Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Harga (Rp) *</label>
                      <input
                        type="number"
                        value={formData.price.amount}
                        onChange={(e) => handleNestedInputChange('price', 'amount', parseFloat(e.target.value) || '')}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 899000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Display Price *</label>
                      <input
                        type="text"
                        value={formData.price.displayPrice}
                        onChange={(e) => handleNestedInputChange('price', 'displayPrice', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 899K/bulan"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Harga Asli (Opsional)</label>
                      <input
                        type="number"
                        value={formData.price.originalAmount}
                        onChange={(e) => handleNestedInputChange('price', 'originalAmount', parseFloat(e.target.value) || '')}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 1099000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Original Display Price (Opsional)</label>
                      <input
                        type="text"
                        value={formData.price.originalDisplayPrice}
                        onChange={(e) => handleNestedInputChange('price', 'originalDisplayPrice', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="contoh: 1.099K/bulan"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.popular}
                          onChange={(e) => handleInputChange('popular', e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Popular</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.availability.isActive}
                          onChange={(e) => handleNestedInputChange('availability', 'isActive', e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Aktif</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Features */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fitur *</label>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleArrayInputChange('features', index, e.target.value)}
                        className="flex-grow border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Masukkan fitur"
                      />
                      {formData.features.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem('features', index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Hapus
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('features')}
                    className="text-green-600 hover:text-green-800 text-sm flex items-center space-x-1"
                  >
                    <Plus size={14} />
                    <span>Tambah Fitur</span>
                  </button>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePackage}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Update Paket
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Package Detail Modal */}
        {showDetailModal && selectedPackage && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Detail Paket</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Basic Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Package size={18} />
                      <span>Informasi Dasar</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Nama:</span> {selectedPackage.name}</div>
                      <div><span className="font-medium">Kategori:</span> {selectedPackage.category}</div>
                      <div><span className="font-medium">Kecepatan:</span> {selectedPackage.speed}</div>
                      {selectedPackage.upload && (
                        <div><span className="font-medium">Upload:</span> {selectedPackage.upload}</div>
                      )}
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                          selectedPackage.availability?.isActive 
                            ? 'bg-green-100 text-green-800 border-green-200' 
                            : 'bg-red-100 text-red-800 border-red-200'
                        }`}>
                          {selectedPackage.availability?.isActive ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </div>
                      {selectedPackage.popular && (
                        <div>
                          <span className="font-medium">Popular:</span> 
                          <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                            Ya
                          </span>
                        </div>
                      )}
                      <div><span className="font-medium">Sort Order:</span> {selectedPackage.sortOrder || 0}</div>
                    </div>
                  </div>

                  {/* Price Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <DollarSign size={18} />
                      <span>Informasi Harga</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Harga:</span> {formatCurrency(selectedPackage.price?.amount)}</div>
                      <div><span className="font-medium">Display Price:</span> {selectedPackage.price?.displayPrice}</div>
                      {selectedPackage.price?.originalAmount && (
                        <>
                          <div><span className="font-medium">Harga Asli:</span> {formatCurrency(selectedPackage.price.originalAmount)}</div>
                          <div><span className="font-medium">Original Display:</span> {selectedPackage.price?.originalDisplayPrice}</div>
                        </>
                      )}
                      <div><span className="font-medium">Currency:</span> {selectedPackage.price?.currency}</div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                    <h4 className="font-semibold text-gray-900 mb-3">Deskripsi</h4>
                    <p className="text-sm text-gray-700">{selectedPackage.description}</p>
                  </div>

                  {/* Features */}
                  {selectedPackage.features && selectedPackage.features.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-3">Fitur</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                        {selectedPackage.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Cities */}
                  {selectedPackage.availability?.cities && selectedPackage.availability.cities.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <MapPin size={18} />
                        <span>Kota Tersedia</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedPackage.availability.cities.map((city, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-200">
                            {city}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specifications */}
                  {selectedPackage.specifications && (
                    <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <Zap size={18} />
                        <span>Spesifikasi</span>
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        {selectedPackage.specifications.downloadSpeed?.value && (
                          <div>
                            <span className="font-medium">Download:</span> {selectedPackage.specifications.downloadSpeed.value} {selectedPackage.specifications.downloadSpeed.unit}
                          </div>
                        )}
                        {selectedPackage.specifications.uploadSpeed?.value && (
                          <div>
                            <span className="font-medium">Upload:</span> {selectedPackage.specifications.uploadSpeed.value} {selectedPackage.specifications.uploadSpeed.unit}
                          </div>
                        )}
                        {selectedPackage.specifications.maxDevices && (
                          <div>
                            <span className="font-medium">Max Devices:</span> {selectedPackage.specifications.maxDevices}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Data Quota:</span> {selectedPackage.specifications.dataQuota || 'Unlimited'}
                        </div>
                        {selectedPackage.specifications.latency?.value && (
                          <div>
                            <span className="font-medium">Latency:</span> {selectedPackage.specifications.latency.value} {selectedPackage.specifications.latency.unit}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireAdminRole>
  );
};

export default AdminPackages;